// World library display contract — pure projections (A4).
import { WIZARD_BUILDER_STEPS } from '../constants.js';
import type {
  LocationGrounding,
  OperatingStatus,
  WizardStepId,
  WorldDraft,
  WorldSnapshot,
} from '../domain/types.js';

export const SUBDESCRIPTION_SNIPPET_MAX = 120;
export const BUILDER_PROGRESS_TOTAL = 4;
export const WORLD_LIBRARY_EMPTY_TITLE = 'No worlds yet';
export const WORLD_LIBRARY_EMPTY_BODY = 'Create your first world to begin.';
export const WORLD_LIBRARY_LEGACY_UNMIGRATABLE =
  'We found older saved data that could not be opened.';
export const WORLD_LIBRARY_SORT_PREF_KEY = 'world_library_sort';

export type WorldLibraryStatus = 'Draft' | 'Ready' | 'Active' | 'Paused';
export type WorldLibrarySort = 'recent' | 'name' | 'created';
export type WorldRecordType = 'draft' | 'world';

export interface WorldListItem {
  id: string;
  recordType: WorldRecordType;
  name: string;
  subdescriptionSnippet: string;
  regionCity: string;
  locationType: string;
  status: WorldLibraryStatus;
  revision: number;
  progressStep: number;
  progressTotal: number;
  updatedAt: string;
  createdAt: string;
  resumeStep: WizardStepId;
}

export interface WorldListItemSource {
  id: string;
  recordType: WorldRecordType;
  name: string;
  subdescriptionSnippet: string;
  regionCity: string;
  locationType: string;
  status: WorldLibraryStatus;
  revision: number;
  wizardStep: WizardStepId;
  updatedAt: string;
  createdAt: string;
}

export function truncateSubdescription(text: string, max = SUBDESCRIPTION_SNIPPET_MAX): string {
  const trimmed = text.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max - 1)}…`;
}

export function deriveLocationName(location: LocationGrounding | null | undefined): string {
  const name = location?.name?.trim() ?? '';
  return name.length > 0 ? name : 'Untitled world';
}

export function deriveRegionCity(location: LocationGrounding | null | undefined): string {
  return location?.region?.trim() ?? '';
}

export function deriveSubdescriptionSnippet(
  connected: WorldDraft['connected'] | WorldSnapshot['connected'] | null | undefined,
): string {
  return truncateSubdescription(connected?.description?.trim() ?? '');
}

export function deriveLocationTypeLabel(
  connected: WorldDraft['connected'] | WorldSnapshot['connected'] | null | undefined,
): string {
  return truncateSubdescription(connected?.locationType?.trim() ?? '');
}

export function builderProgressStep(wizardStep: WizardStepId): number {
  if (wizardStep === 'welcome') return 0;
  const idx = WIZARD_BUILDER_STEPS.indexOf(
    wizardStep as (typeof WIZARD_BUILDER_STEPS)[number],
  );
  if (idx >= 0) return idx + 1;
  return BUILDER_PROGRESS_TOTAL;
}

export function deriveWorldLibraryStatus(
  wizardStep: WizardStepId,
  operatingStatus?: OperatingStatus,
): WorldLibraryStatus {
  if (wizardStep === 'launched') {
    return operatingStatus === 'closed' ? 'Paused' : 'Active';
  }
  if (wizardStep === 'ready-to-launch') return 'Ready';
  return 'Draft';
}

export function worldListItem(source: WorldListItemSource): WorldListItem {
  return {
    id: source.id,
    recordType: source.recordType,
    name: source.name,
    subdescriptionSnippet: source.subdescriptionSnippet,
    regionCity: source.regionCity,
    locationType: source.locationType,
    status: source.status,
    revision: source.revision,
    progressStep: builderProgressStep(source.wizardStep),
    progressTotal: BUILDER_PROGRESS_TOTAL,
    updatedAt: source.updatedAt,
    createdAt: source.createdAt,
    resumeStep: source.wizardStep,
  };
}

export function libraryMetadataFromDraft(
  draft: WorldDraft,
  revision: number,
): Omit<WorldListItemSource, 'id' | 'recordType'> {
  return {
    name: deriveLocationName(draft.location),
    subdescriptionSnippet: deriveSubdescriptionSnippet(draft.connected),
    regionCity: deriveRegionCity(draft.location),
    locationType: deriveLocationTypeLabel(draft.connected),
    status: deriveWorldLibraryStatus(draft.wizardStep),
    revision,
    wizardStep: draft.wizardStep,
    updatedAt: draft.updatedAt,
    createdAt: draft.createdAt,
  };
}

export function libraryMetadataFromSnapshot(
  snapshot: WorldSnapshot,
  revision = 1,
): Omit<WorldListItemSource, 'id' | 'recordType'> {
  const wizardStep: WizardStepId = 'launched';
  return {
    name: deriveLocationName(snapshot.location),
    subdescriptionSnippet: deriveSubdescriptionSnippet(snapshot.connected),
    regionCity: deriveRegionCity(snapshot.location),
    locationType: deriveLocationTypeLabel(snapshot.connected),
    status: deriveWorldLibraryStatus(wizardStep, snapshot.operatingStatus),
    revision,
    wizardStep,
    updatedAt: snapshot.acceptedAt,
    createdAt: snapshot.acceptedAt,
  };
}

export function compareWorldListItems(
  a: WorldListItem,
  b: WorldListItem,
  sort: WorldLibrarySort,
): number {
  switch (sort) {
    case 'name':
      return (
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }) ||
        b.updatedAt.localeCompare(a.updatedAt)
      );
    case 'created':
      return (
        b.createdAt.localeCompare(a.createdAt) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
    default:
      return (
        b.updatedAt.localeCompare(a.updatedAt) ||
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
  }
}
